// src/pages/accounting/SalaryCostPage.jsx
// ─────────────────────────────────────────────────────────
// Employment filtering:
//   Start: COALESCE(users.join_date, user_profiles.joining_date)
//     (users.start_date was bulk-overwritten and is NOT reliable)
//   End:   COALESCE(users.end_date, user_profiles.leaving_date)
//   Employed if: start <= monthEnd AND (end IS NULL OR end >= monthStart)
//   Missing start → excluded (data issue, logged in dev)
//   Employment gate ALWAYS runs first — never bypassed by toggle
//
// Salary resolution (STRICT CUTOVER):
//   Pre-2026:
//     1. Ledger row (legacy_import) → use it
//     2. No ledger row → "missing" (NEVER fall back to salary matrix)
//   Post-2026, NL office users:
//     1. Ledger row with source==='override' → use it (individual override wins)
//     2. NL salary matrix via getInternalCostRate → native EUR (primary)
//     3. Derived ledger row → fallback if matrix empty
//     4. Neither → "missing"
//   Post-2026, IN office users:
//     1. Ledger row (derived/override) → use it
//     2. getInternalCostRate (IN salary matrix) → fallback (employment-guarded)
//     3. Neither → "missing"
//
// Active/All toggle:
//   - "Active Users": show users where is_active === true (current status only)
//   - "All Users": show all users who pass employment window for the selected month
// ─────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioUserIcon,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { useSalaryLedger } from '../../hooks/useSalaryLedger';
import { useSettings } from '../../hooks/useSettings';
import { supabase } from '../../supabaseClient';
import { prorateToHours, isFteSalarySource, STANDARD_FTE_HOURS } from './salaryCostHelpers';
import { employedMonthFraction } from '../../utils/timesheetHelpers';

const PRESENTATION_CODE = '1a';
// Cutover: use local Date constructor (year, monthIndex, day) to avoid
// UTC-midnight timezone shift that makes Jan 2026 appear as Dec 2025.
const CUTOVER_DATE = new Date(2026, 0, 1);

// ════════════════════════════════════════════════════════
// SHARED HELPERS (outside component — no re-creation)
// ════════════════════════════════════════════════════════

/** Resolve auth UUID — tries ALL known property name variants */
const resolveAuthId = (u) =>
  u.auth_user_id ||
  u.authUserId ||
  u.authId ||
  u.authUUID ||
  u.user_uuid ||
  u.userUuid ||
  null;

/** Resolve employment dates from all available sources.
 *
 *  START (priority order):
 *    1. users.join_date   → u.joinDate  (authoritative)
 *    2. user_profiles.joining_date → u.joiningDate  (fallback)
 *    users.start_date is deliberately excluded (bulk-overwritten, unreliable)
 *
 *  END (priority order):
 *    1. users.end_date    → u.endDate   (authoritative)
 *    2. user_profiles.leaving_date → u.leavingDate  (fallback)
 */
const resolveEmploymentDates = (u) => {
  const startVal =
    u.join_date || u.joinDate ||           // primary: users.join_date
    u.joining_date || u.joiningDate ||     // fallback: user_profiles.joining_date
    null;                                  // start_date deliberately excluded

  const endVal =
    u.end_date || u.endDate ||             // primary: users.end_date
    u.leaving_date || u.leavingDate ||     // fallback: user_profiles.leaving_date
    null;

  return { startVal, endVal };
};

/** Parse date value to local Date. Returns null if invalid.
 *  YYYY-MM-DD strings are parsed via regex → new Date(y, m-1, d) to avoid
 *  the UTC-midnight timezone bug where '2026-01-01' becomes Dec 31 2025
 *  in negative-offset timezones (IST, etc.).
 */
const parseDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const s = String(val);
  // Match YYYY-MM-DD (with optional trailing time/timezone)
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  // Fallback for non-ISO formats (rare)
  const f = new Date(val);
  return isNaN(f.getTime()) ? null : f;
};

/**
 * Build employment status for a user in a given month.
 * Uses simple Date comparisons — Supabase returns ISO strings.
 *
 * employed if: join_date <= monthEnd AND (end_date is null OR end_date >= monthStart)
 * If join_date is missing: NOT employed (log warning in dev).
 */
const buildEmploymentStatusAtMonth = (u, year, month) => {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0); // last day of month

  const { startVal, endVal } = resolveEmploymentDates(u);
  const startDate = parseDate(startVal);
  const endDate = parseDate(endVal);

  // If join_date is missing, user should not appear (data issue)
  if (!startDate) {
    return { monthStart, monthEnd, startVal, endVal, startDate, endDate, startKnown: false, endKnown: !!endDate, employed: false };
  }

  // employed = joined on or before month end AND (no end date OR ended on or after month start)
  const employed = startDate <= monthEnd && (!endDate || endDate >= monthStart);

  return {
    monthStart,
    monthEnd,
    startVal,
    endVal,
    startDate,
    endDate,
    startKnown: true,
    endKnown: !!endDate,
    employed,
  };
};

// Check both snake_case (Supabase raw) and camelCase (mapped) variants.
// Toggle filters by current system status (is_active flag) only — NOT employment or status field.
const isInactiveUser = (u) => u.is_active === false || u.isActive === false;

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const sourceBadgeStyle = (src) => {
  const styles = {
    missing:             { bg: '#FFF3E0', color: '#E65100' },
    legacy:              { bg: '#E8EAF6', color: '#283593' },
    derived:             { bg: '#E8F5E9', color: '#2E7D32' },
    override:            { bg: '#FFF8E1', color: '#F57F17' },
    salary_matrix:       { bg: '#E3F2FD', color: '#1565C0' },
    salary_matrix_nl:    { bg: '#E0F2FE', color: '#0369a1' },
    individual_override: { bg: '#FFF8E1', color: '#F57F17' },
    user_base:           { bg: '#F3E5F5', color: '#7B1FA2' },
  };
  const s = styles[src] || { bg: '#F5F5F5', color: '#757575' };
  return { padding: '2px 6px', borderRadius: '4px', background: s.bg, color: s.color };
};

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════

export default function SalaryCostPage({
  activeUsers,
  activeTeams,
  brioColors,
  getInternalCostRate,
  getClientBillingRate,
  getEmployerBurdenShare,
  fmtCurr,
  currSymbol = '₹',
  isAdmin = false,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const { getAllUsersForMonth, rebuildAllLedgers, loading: ledgerLoading } = useSalaryLedger();
  const { manualSalaryMarkup, essentialProfitPercentage, formatMoneyInDisplayCurrency, baseCurrency } = useSettings();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [showAll, setShowAll] = useState(false);
  const [rebuildStatus, setRebuildStatus] = useState(null); // null | 'rebuilding' | 'done' | 'error'
  const [rebuildError, setRebuildError] = useState(null);

  const selectedMonthStart = new Date(selectedYear, selectedMonth - 1, 1);
  const isPreCutover = selectedMonthStart < CUTOVER_DATE;

  // ════════════════════════════════════════════════
  // PER-USER CONTEXT — computed once, reused everywhere
  // ════════════════════════════════════════════════

  const userContextById = useMemo(() => {
    const m = {};
    for (const u of activeUsers) {
      const authId = resolveAuthId(u);
      const employment = buildEmploymentStatusAtMonth(u, selectedYear, selectedMonth);
      m[u.id] = {
        user: u,
        authId,
        employment,
        inactive: isInactiveUser(u),
        active: !isInactiveUser(u),
      };
    }

    // Dev diagnostics — shows EXACTLY what fields are on user objects
    if (process.env.NODE_ENV !== 'production') {
      const contexts = Object.values(m);
      const noAuthId = contexts.filter(c => !c.authId);
      const noStart = contexts.filter(c => !c.employment.startKnown);
      const withEnd = contexts.filter(c => c.employment.endKnown);
      const endedBefore = contexts.filter(c => c.employment.endKnown && !c.employment.employed);
      if (noAuthId.length > 0) {
        console.warn(
          `[SalaryCostPage] ${noAuthId.length} user(s) missing auth UUID — ledger can never match:`,
          noAuthId.slice(0, 5).map(c => c.user.name)
        );
      }
      if (noStart.length > 0) {
        console.warn(
          `[SalaryCostPage] ${noStart.length} user(s) missing join_date — EXCLUDED from display:`,
          noStart.slice(0, 5).map(c => c.user.name)
        );
      }
      // ── Field-level diagnostics: show actual values on user objects ──
      const sample = contexts.slice(0, 3);
      console.log(`[SalaryCostPage] 📊 Field values (first ${sample.length} users):`);
      sample.forEach(c => {
        const u = c.user;
        console.log(
          `  ${u.name}: ` +
          `joinDate=${JSON.stringify(u.joinDate)} joiningDate=${JSON.stringify(u.joiningDate)} ` +
          `endDate=${JSON.stringify(u.endDate)} leavingDate=${JSON.stringify(u.leavingDate)} ` +
          `→ resolved start=${c.employment.startVal || '∅'} end=${c.employment.endVal || '∅'} employed=${c.employment.employed}`
        );
      });
      // Show end-date summary
      if (withEnd.length > 0) {
        console.log(`[SalaryCostPage] ✅ ${withEnd.length} user(s) have employment end date, ${endedBefore.length} ended before this month`);
        withEnd.slice(0, 5).forEach(c => {
          console.log(`  ${c.user.name}: end=${c.employment.endVal} employed=${c.employment.employed}`);
        });
      } else {
        console.warn(
          '[SalaryCostPage] ⚠️ ZERO users have employment end dates. ' +
          'Check: (1) users.end_date column exists? (2) user_profiles query succeeded? (3) profileMap keys match user ids?'
        );
      }
    }

    return m;
  }, [activeUsers, selectedYear, selectedMonth]);

  // ════════════════════════════════════════════════
  // FILTERING — employment gate ALWAYS runs first
  // ════════════════════════════════════════════════

  const displayedUsers = useMemo(() => {
    const all = Object.values(userContextById);

    // Step 1: ALWAYS filter by employment range — no bypass
    const eligible = all.filter(ctx => ctx.employment.employed);

    // Step 2: Active Users / All Users toggle
    //   "Active Users" (default): show only users where is_active === true (current system status)
    //   "All Users": show everyone who passes the employment window for the selected month
    if (showAll) {
      return eligible.map(ctx => ctx.user);
    }

    const isUserActive = (u) => u.is_active === true || u.isActive === true;
    return eligible.filter(ctx => isUserActive(ctx.user)).map(ctx => ctx.user);
  }, [userContextById, showAll]);

  // Dev diagnostics
  if (process.env.NODE_ENV !== 'production') {
    const allCtx = Object.values(userContextById);
    const employedCount = allCtx.filter(c => c.employment.employed).length;
    const notEmployed = allCtx.filter(c => !c.employment.employed);
    const inactiveInDisplayed = displayedUsers.filter(u => isInactiveUser(u));
    console.log(
      `[SalaryCostPage] month=${selectedYear}-${String(selectedMonth).padStart(2, '0')}`,
      `total=${activeUsers.length}`,
      `employed=${employedCount}`,
      `displayed=${displayedUsers.length}`,
      `inactiveInDisplay=${inactiveInDisplayed.length}`,
      `excluded=${notEmployed.length}`,
      `cutover=${isPreCutover ? 'PRE-2026' : 'POST-2026'}`,
      `filter=${showAll ? 'All Users' : 'Active Users'}`,
    );
    if (notEmployed.length > 0 && notEmployed.length <= 10) {
      console.log('[SalaryCostPage] excluded:', notEmployed.map(c => {
        const e = c.employment;
        return `${c.user.name} (start=${e.startVal || '?'} end=${e.endVal || '?'})`;
      }));
    }
  }

  // ════════════════════════════════════════════════
  // LEDGER FETCH
  // ════════════════════════════════════════════════

  const [ledgerByUser, setLedgerByUser] = useState({});
  const [ledgerFetched, setLedgerFetched] = useState(false);

  // Contracted hours per user for selected month (via RPC: get_contracted_hours_for_month)
  const [contractedHoursByUserId, setContractedHoursByUserId] = useState({});

  const fetchLedger = useCallback(async () => {
    setLedgerFetched(false);
    const rows = await getAllUsersForMonth(selectedYear, selectedMonth);
    const map = {};
    (rows || []).forEach(r => { map[r.auth_user_id] = r; });
    setLedgerByUser(map);
    setLedgerFetched(true);
  }, [getAllUsersForMonth, selectedYear, selectedMonth]);

  // Fetch contracted hours per user for the selected month via RPC (batched)
  // RPC: get_contracted_hours_for_month(p_user_id BIGINT, p_month DATE)
  // Reads from user_role_history.contracted_hours for the overlapping period.
  // Stable string key — changes only when the actual set of users changes, not
  // on every realtime event that returns a new array reference for the same users.
  const activeUserIds = useMemo(
    () => (activeUsers || []).map(u => u.id).sort().join(','),
    [activeUsers]
  );

  const fetchContractedHours = useCallback(async () => {
    if (!activeUsers || activeUsers.length === 0) return;

    const pMonth = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;

    try {
      const results = await Promise.all(
        activeUsers.map(async (user) => {
          try {
            const { data, error } = await supabase.rpc('get_contracted_hours_for_month', {
              p_user_id: user.id,
              p_month:   pMonth,
            });
            if (error) {
              return { userId: user.id, name: user.name, hours: null, err: error.message };
            }
            return { userId: user.id, name: user.name, hours: data, err: null };
          } catch (err) {
            return { userId: user.id, name: user.name, hours: null, err: err.message };
          }
        })
      );

      const hoursMap = {};
      let resolved = 0;
      const failed = [];
      for (const r of results) {
        if (r.hours != null) {
          hoursMap[r.userId] = r.hours;
          resolved++;
        } else if (r.err) {
          failed.push(r);
        }
      }
      setContractedHoursByUserId(hoursMap);

      if (process.env.NODE_ENV !== 'production') {
        console.log(
          `[SalaryCostPage] contractedHours RPC: ${resolved}/${activeUsers.length} resolved for ${pMonth}`
        );
        if (failed.length > 0) {
          console.warn(
            `[SalaryCostPage] contractedHours RPC failed for ${failed.length} user(s):`,
            failed.slice(0, 5).map(f => `${f.name} — ${f.err}`)
          );
        }
        // Warn about users with no role history coverage (will default to 176)
        const missing = results.filter(r => r.hours == null && !r.err);
        if (missing.length > 0) {
          console.warn(
            `[SalaryCostPage] Missing contracted_hours in role history for ${missing.length} user(s) in ${pMonth}; defaulting to 176:`,
            missing.slice(0, 5).map(m => m.name)
          );
        }
      }
    } catch (err) {
      console.error('[SalaryCostPage] fetchContractedHours batch error:', err);
      setContractedHoursByUserId({});
    }
  }, [activeUserIds, selectedYear, selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLedger(); fetchContractedHours(); }, [fetchLedger, fetchContractedHours]);

  // ════════════════════════════════════════════════
  // REBUILD — TRUE RECREATE (delete stale + regenerate)
  // Sends a bounded 24-month window so stale rows past end_date are cleaned.
  // ════════════════════════════════════════════════

  const handleRebuild = async () => {
    if (isPreCutover) {
      alert('Pre-2026 salary data comes from legacy imports only. Rebuild is not available for months before January 2026.');
      return;
    }

    setRebuildStatus('rebuilding');
    setRebuildError(null);
    try {
      const fromMonth = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;

      // Compute toMonth = selected month + 24 months (bounded rebuild window)
      const toDate = new Date(selectedYear, selectedMonth - 1 + 24, 1);
      const toMonth = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-01`;

      const result = await rebuildAllLedgers(fromMonth, toMonth);

      if (result?.success === false) {
        const msg = result.error || 'Unknown error';
        console.error('[SalaryCostPage] Rebuild failed:', msg);
        setRebuildError(msg);
        setRebuildStatus('error');
      } else {
        console.log('[SalaryCostPage] Rebuild complete:', result);
        setRebuildStatus('done');
      }

      // Always refetch ledger + contracted hours after rebuild
      await fetchLedger();
      await fetchContractedHours();
    } catch (err) {
      const msg = err?.message || String(err);
      console.error('[SalaryCostPage] Rebuild error:', msg);
      setRebuildError(msg);
      setRebuildStatus('error');
    }

    // Clear status after 3 seconds
    setTimeout(() => setRebuildStatus(null), 3000);
  };

  // ════════════════════════════════════════════════
  // MARKUP FACTOR — reads from app_settings.manualSalaryMarkup
  // Source of truth: useSettings().manualSalaryMarkup (year→numeric map)
  // If missing for the year → 0 (never 1).
  // ════════════════════════════════════════════════

  const resolveMarkupFactor = useCallback(() => {
    const markupMap = manualSalaryMarkup || {};
    const rawValue = markupMap[selectedYear] ?? markupMap[String(selectedYear)];
    if (typeof rawValue === 'number' && rawValue > 0) return rawValue;
    return 0;
  }, [manualSalaryMarkup, selectedYear]);

  // ════════════════════════════════════════════════
  // SALARY RESOLUTION — STRICT CUTOVER ENFORCEMENT
  //
  //   Pre-2026: ledger only → "missing" if absent (NO matrix fallback)
  //   Post-2026: ledger > getInternalCostRate > "missing"
  // ════════════════════════════════════════════════

  const resolveSalary = useCallback((user) => {
    const ctx = userContextById[user.id];
    if (!ctx?.employment?.employed) {
      return { baseSalary: null, bonus: null, contractedHours: null, hourlyCost: null, source: 'excluded', markupFactor: 0, totalMonthlyCost: null, currency: baseCurrency };
    }

    const authId = ctx.authId;
    const ledgerRow = authId ? ledgerByUser[authId] : null;
    const isNLUser = (user.officeLocation || 'NL') === 'NL';
    const hasLedgerSalary = ledgerRow && (ledgerRow.base_salary > 0 || ledgerRow.bonus > 0);

    let raw;

    // ── Post-2026 NL users: NL salary matrix is the primary source (native EUR) ──
    // Priority: explicit override ledger row > NL matrix > derived ledger row > missing
    if (!isPreCutover && isNLUser) {
      const isLedgerOverride = hasLedgerSalary && ledgerRow.source === 'override';

      if (isLedgerOverride) {
        // Individual salary override always wins
        const bs = ledgerRow.base_salary || 0;
        const bn = ledgerRow.bonus || 0;
        const ch = contractedHoursByUserId[user.id] ?? 176;
        raw = {
          baseSalary: bs,
          bonus: bn,
          contractedHours: ch,
          hourlyCost: (bs + bn) / 176, // Full-time equivalent monthly salary ÷ standard hours
          source: 'override',
          currency: baseCurrency,
        };
      } else if (!ledgerFetched) {
        raw = { baseSalary: null, bonus: null, contractedHours: null, hourlyCost: null, source: '...', currency: 'EUR' };
      } else if (getInternalCostRate && ctx.employment.employed) {
        // Read NL salary matrix (returns EUR amounts)
        const internal = getInternalCostRate(user.id, selectedYear, selectedMonth);
        if (internal && (internal.monthlySalary > 0 || internal.monthlyBonusEquivalent > 0)) {
          const bs = internal.monthlySalary || 0;
          const bn = internal.monthlyBonusEquivalent || 0;
          const ch = contractedHoursByUserId[user.id] ?? 176;
          raw = {
            baseSalary: bs,
            bonus: bn,
            contractedHours: ch,
            hourlyCost: (bs + bn) / 176, // Full-time equivalent monthly salary ÷ standard hours
            source: internal.source || 'salary_matrix_nl',
            currency: internal.currency || 'EUR',
          };
        } else if (hasLedgerSalary) {
          // NL matrix empty — fall back to derived ledger row
          const bs = ledgerRow.base_salary || 0;
          const bn = ledgerRow.bonus || 0;
          const ch = contractedHoursByUserId[user.id] ?? 176;
          raw = {
            baseSalary: bs,
            bonus: bn,
            contractedHours: ch,
            hourlyCost: (bs + bn) / 176, // Full-time equivalent monthly salary ÷ standard hours
            source: ledgerRow.source || 'derived',
            currency: baseCurrency,
          };
        } else {
          // No salary found from role history lookup — role/level may not be configured
          raw = { baseSalary: null, bonus: null, contractedHours: contractedHoursByUserId[user.id] ?? 176, hourlyCost: null, source: 'missing', currency: 'EUR' };
        }
      } else if (hasLedgerSalary) {
        // getInternalCostRate not available — use ledger
        const bs = ledgerRow.base_salary || 0;
        const bn = ledgerRow.bonus || 0;
        const ch = contractedHoursByUserId[user.id] ?? 176;
        raw = {
          baseSalary: bs,
          bonus: bn,
          contractedHours: ch,
          hourlyCost: (bs + bn) / 176, // Full-time equivalent monthly salary ÷ standard hours
          source: ledgerRow.source || 'derived',
          currency: baseCurrency,
        };
      } else {
        raw = { baseSalary: null, bonus: null, contractedHours: contractedHoursByUserId[user.id] ?? 176, hourlyCost: null, source: 'missing', currency: 'EUR' };
      }
    } else {
      // ── IN users (any period) + NL users pre-2026 ──

      // 1. Ledger row with actual non-zero salary → trust it
      if (hasLedgerSalary) {
        const bs = ledgerRow.base_salary || 0;
        // For pre-2026 months: if the admin has explicitly configured bonuses via
        // the team member modal (users.bonuses[year]), prefer those over whatever
        // the legacy CSV import stored in ledger.bonus.  The modal is the intended
        // UI for retrospectively setting/correcting bonuses on historical data.
        // For post-2026 IN users: the ledger.bonus is derived from salary_driver_events
        // (the canonical source), so we trust it as-is.
        let bn;
        if (isPreCutover) {
          const modalYearBonuses = user.bonuses?.[selectedYear];
          bn = modalYearBonuses !== undefined
            ? ((modalYearBonuses.q1 || 0) + (modalYearBonuses.q2 || 0) +
               (modalYearBonuses.q3 || 0) + (modalYearBonuses.q4 || 0)) / 12
            : (ledgerRow.bonus || 0);
        } else {
          bn = ledgerRow.bonus || 0;
        }
        const ch = contractedHoursByUserId[user.id] ?? 176;
        raw = {
          baseSalary: bs,
          bonus: bn,
          contractedHours: ch,
          hourlyCost: (bs + bn) / 176, // Full-time equivalent monthly salary ÷ standard hours
          source: ledgerRow.source === 'legacy_import' ? 'legacy' : (ledgerRow.source || 'derived'),
          currency: baseCurrency, // ledger always stored in base currency (INR)
        };
      }

      // ── STRICT CUTOVER: Pre-2026 NEVER falls back to salary matrix ──
      if (!raw && isPreCutover) {
        if (!ledgerFetched) {
          raw = { baseSalary: null, bonus: null, contractedHours: null, hourlyCost: null, source: '...', currency: baseCurrency };
        } else {
          raw = { baseSalary: null, bonus: null, contractedHours: contractedHoursByUserId[user.id] ?? 176, hourlyCost: null, source: 'missing', currency: baseCurrency };
        }
      }

      // ── Post-2026 IN users: IN salary matrix fallback ──
      if (!raw && ledgerFetched && getInternalCostRate && ctx.employment.employed) {
        const internal = getInternalCostRate(user.id, selectedYear, selectedMonth);
        if (internal && (internal.monthlySalary > 0 || internal.monthlyBonusEquivalent > 0)) {
          const bs = internal.monthlySalary || 0;
          const bn = internal.monthlyBonusEquivalent || 0;
          const ch = contractedHoursByUserId[user.id] ?? 176;
          raw = {
            baseSalary: bs,
            bonus: bn,
            contractedHours: ch,
            hourlyCost: (bs + bn) / 176, // Full-time equivalent monthly salary ÷ standard hours
            source: internal.source || 'salary_matrix',
            currency: internal.currency || baseCurrency,
          };
        }
      }

      // Still loading
      if (!raw && !ledgerFetched) {
        raw = { baseSalary: null, bonus: null, contractedHours: null, hourlyCost: null, source: '...', currency: baseCurrency };
      }

      // No data
      if (!raw) {
        raw = { baseSalary: null, bonus: null, contractedHours: contractedHoursByUserId[user.id] ?? 176, hourlyCost: null, source: 'missing', currency: baseCurrency };
      }
    }

    // ── Part-time pro-ration ──────────────────────────────────────────────
    // Full-time staff cost their full monthly salary. A part-timer costs a
    // pro-rata share: a 60%-FTE employee (≈106 of 176 h) costs 60% of the
    // full-time salary. So when contractedHours < full-time, scale the monthly
    // salary (and bonus) by contractedHours / 176; the hourly rate stays the
    // FTE rate (salary ÷ 176). raw.contractedHours already resolves correctly
    // per user (get_contracted_hours_for_month → salaried role-history hours,
    // or a freelancer's approved hours for the month), so freelancers flow
    // through the same path. Full-time months (176 h) are unaffected.
    //
    // Guard: only pro-rate full-time-EQUIVALENT salary figures. Legacy imports
    // and overrides already store an ACTUAL amount (e.g. IN freelancers billed
    // hourly), so pro-rating those would double-count (see isFteSalarySource).
    const isProrated = raw.baseSalary !== null
      && raw.contractedHours != null
      && raw.contractedHours < STANDARD_FTE_HOURS
      && isFteSalarySource(raw.source, user.officeLocation);
    let displayBaseSalary = raw.baseSalary;
    let displayBonus = raw.bonus;
    if (isProrated) {
      displayBaseSalary = prorateToHours(raw.baseSalary, raw.contractedHours);
      displayBonus = prorateToHours(raw.bonus, raw.contractedHours);
    }

    // ── Partial-month pro-ration (mid-month joiner / leaver) ──────────────
    // A salaried employee who starts or leaves mid-month is paid pro-rata for
    // that first / last month. Scale the (already part-time-adjusted) salary by
    // the fraction of the month's working days they were employed — 1 for full
    // months. FTE-source salaries only (legacy/override are already actuals).
    const canProrateMonth = raw.baseSalary !== null && isFteSalarySource(raw.source, user.officeLocation);
    const employedFraction = canProrateMonth
      ? employedMonthFraction(ctx?.employment?.startVal, ctx?.employment?.endVal, selectedYear, selectedMonth)
      : 1;
    const isPartialMonth = employedFraction < 1;
    if (isPartialMonth) {
      displayBaseSalary = (displayBaseSalary || 0) * employedFraction;
      displayBonus = (displayBonus || 0) * employedFraction;
    }

    // ── Augment with markup factor and total monthly cost ──
    const markupFactor = resolveMarkupFactor();
    const totalMonthlyCost = raw.baseSalary !== null
      ? ((displayBaseSalary || 0) + (displayBonus || 0)) * markupFactor
      : null;

    const isAdjusted = isProrated || isPartialMonth;
    const prorationNote = !isAdjusted ? null : [
      isProrated ? `part-time ${raw.contractedHours}/${STANDARD_FTE_HOURS} h` : null,
      isPartialMonth ? `${Math.round(employedFraction * 100)}% of month employed` : null,
    ].filter(Boolean).join(' × ');

    return {
      ...raw,
      baseSalary: raw.baseSalary === null ? null : displayBaseSalary,
      bonus: raw.baseSalary === null ? raw.bonus : displayBonus,
      isProrated,
      isPartialMonth,
      isAdjusted,
      prorationNote,
      markupFactor,
      totalMonthlyCost,
    };
  }, [userContextById, ledgerByUser, ledgerFetched, getInternalCostRate, selectedYear, selectedMonth, isPreCutover, resolveMarkupFactor, contractedHoursByUserId, baseCurrency]);

  // ════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════

  const debugStats = useMemo(() => {
    if (process.env.NODE_ENV === 'production') return null;
    const allCtx = Object.values(userContextById);
    const employedCount = allCtx.filter(c => c.employment.employed).length;
    const withEnd = allCtx.filter(c => c.employment.endKnown).length;
    const endedBefore = allCtx.filter(c => c.employment.endKnown && !c.employment.employed).length;
    return { employedCount, withEnd, endedBefore };
  }, [userContextById]);

  // Rebuild button label
  const rebuildLabel = rebuildStatus === 'rebuilding' ? 'Rebuilding…'
    : rebuildStatus === 'done' ? '✓ Done'
    : rebuildStatus === 'error' ? '✗ Error'
    : 'Rebuild';

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">
                Monthly salary cost from ledger — contracted hours and internal cost rate
                <span style={{ marginLeft: 8, color: 'var(--brio-gold, #c5a572)', fontSize: 11, fontWeight: 600 }}>
                  Essential profit target: {essentialProfitPercentage}%
                </span>
                {isPreCutover && (
                  <span style={{ marginLeft: 8, color: '#283593', fontSize: 11, fontWeight: 600 }}>
                    [LEGACY — pre-2026 data from import only]
                  </span>
                )}
                {process.env.NODE_ENV !== 'production' && debugStats && (
                  <span style={{ marginLeft: 12, color: '#999', fontSize: 11 }}>
                    [DEBUG: {activeUsers.length} total |
                    {' '}{debugStats.employedCount} employed this month |
                    {' '}{displayedUsers.length} displayed |
                    {' '}{debugStats.withEnd} have end_date |
                    {' '}{debugStats.endedBefore} ended before month |
                    {' '}filtered out: {activeUsers.length - displayedUsers.length}]
                  </span>
                )}
              </p>
            </div>
            <div className="brio-page-header__right" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Active Users / All Users toggle */}
              <button
                onClick={() => setShowAll(!showAll)}
                className={`brio-btn ${!showAll ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
              >
                {showAll ? 'All Users' : 'Active Users'}
              </button>
              {/* Month selector */}
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="brio-modal-select"
                style={{ width: 'auto' }}
              >
                {monthNames.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="brio-modal-select"
                style={{ width: 'auto' }}
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const y = new Date().getFullYear() - 5 + i;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
              {/* Rebuild button — post-2026 only, admin only */}
              {isAdmin && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                  <button
                    onClick={handleRebuild}
                    disabled={ledgerLoading || isPreCutover || rebuildStatus === 'rebuilding'}
                    className="brio-btn brio-btn-secondary"
                    title={isPreCutover
                      ? 'Rebuild disabled for pre-2026 months (legacy import only)'
                      : 'Rebuild ledger: delete stale + regenerate for next 24 months'
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: isPreCutover ? 0.4 : 1,
                      cursor: isPreCutover ? 'not-allowed' : undefined,
                    }}
                  >
                    <RefreshCw className={`w-4 h-4 ${rebuildStatus === 'rebuilding' ? 'animate-spin' : ''}`} />
                    {rebuildLabel}
                  </button>
                  {rebuildStatus === 'error' && rebuildError && (
                    <div style={{ fontSize: '11px', color: 'var(--brio-orange)', maxWidth: '260px', lineHeight: '1.3' }}>
                      {rebuildError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </BrioFrame>
      </div>

      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th className="text-right">Monthly Salary</th>
                    <th className="text-right">Monthly Bonus</th>
                    <th
                      className="text-right"
                      style={{ cursor: 'help' }}
                      title={
                        'Employer Burden = this employee’s share of the firm’s NL Pension + Salary Tax for the month.\n\n' +
                        'Source: office_overheads_yearly rows for category “Pension” and “Salary Tax”, which are stored QUARTERLY (Q1–Q4) in INR.\n\n' +
                        'Distribution: each quarter’s total is divided across the three months of that quarter, then split among eligible NL non-director employees in proportion to each user’s base salary + bonus for those three months. Directors are excluded (separate NL tax/pension regime); IN users show —.\n\n' +
                        'This figure is included in Direct Labour on the Project Profitability and Project Cost Ledger reports (it is NOT in Op. Overhead there).'
                      }
                    >
                      Burden
                    </th>
                    <th className="text-right">Contracted Hours</th>
                    <th className="text-right">Hourly Cost</th>
                    <th className="text-right">Markup</th>
                    <th className="text-right">Total Monthly Cost</th>
                    <th className="text-right" style={{ width: '80px' }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedUsers.map(user => {
                    const ctx = userContextById[user.id];
                    const { baseSalary, bonus, contractedHours, hourlyCost, source, markupFactor, totalMonthlyCost, currency, isAdjusted, prorationNote } = resolveSalary(user);
                    const team = activeTeams.find(t => t.id === user.teamId);
                    const emp = ctx?.employment;
                    const isNL = (user.officeLocation || 'NL') === 'NL';
                    // FX date for NL salary display: mid-month of selected period
                    const fxDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-15`;
                    // Format a salary amount respecting its source currency
                    const fmtSal = (amount) => {
                      if (currency && currency !== baseCurrency) {
                        return formatMoneyInDisplayCurrency(amount, currency, fxDate);
                      }
                      return fmtCurr(amount);
                    };

                    return (
                      <tr key={user.id} className="brio-row--hoverable">
                        <td>
                          <div className="brio-flex brio-items-center brio-gap-s">
                            <BrioUserIcon
                              initials={getInitials(user.name)}
                              fillColor={brioColors.roles[user.jobRole] || '#D4C4A0'}
                              ringColor={team?.outlineColor || '#5B7A99'}
                              size={32}
                              ringWidth={4}
                            />
                            <div>
                              <div className="brio-font-medium" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {user.name}
                                <span style={{ fontSize: '9px', fontWeight: 600, padding: '1px 5px', borderRadius: '3px', background: isNL ? '#e0f2fe' : '#fef3c7', color: isNL ? '#0369a1' : '#92400e', letterSpacing: '0.05em' }}>
                                  {isNL ? 'NL' : 'IN'}
                                </span>
                              </div>
                              <div className="brio-text-xs brio-text-grey brio-italic">
                                {isNL
                                  ? `Schaal ${(user.effectiveJobRole ?? user.jobRole) || '—'} / Trede ${user.effectiveJobLevel ?? user.jobLevel ?? '—'}`
                                  : `${user.effectiveJobRole ?? user.jobRole} - L${user.effectiveJobLevel ?? user.jobLevel}`
                                }
                                {process.env.NODE_ENV !== 'production' && emp && (
                                  <span style={{ color: '#999', marginLeft: 8 }}>
                                    [join: {emp.startVal ? String(emp.startVal).split('T')[0] : '—'} |
                                    {' '}end: {emp.endVal ? String(emp.endVal).split('T')[0] : '—'}]
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right">
                          {baseSalary !== null ? (
                            <div
                              className="brio-font-medium"
                              title={isAdjusted ? `Pro-rated: ${prorationNote}` : undefined}
                            >
                              {fmtSal(baseSalary)}
                              {currency && currency !== baseCurrency && (
                                <span style={{ fontSize: '9px', color: '#0369a1', marginLeft: '3px' }}>EUR</span>
                              )}
                            </div>
                          ) : (
                            <div
                              className="brio-font-medium"
                              style={{ color: source === 'missing' ? 'var(--brio-orange)' : 'var(--brio-grey)' }}
                            >
                              {source === 'missing' ? '—' : '...'}
                            </div>
                          )}
                        </td>
                        <td className="text-right">
                          {bonus !== null ? (
                            <div className="brio-font-medium" style={{ color: bonus > 0 ? 'var(--brio-green)' : undefined }}>
                              {fmtSal(bonus)}
                            </div>
                          ) : (
                            <div className="brio-font-medium" style={{ color: 'var(--brio-grey)' }}>—</div>
                          )}
                        </td>
                        <td className="text-right">
                          {(() => {
                            // Burden is stored in INR on office_overheads_yearly; format in
                            // the user's salary currency (EUR for NL, INR for IN) so it lines
                            // up with the Salary / Bonus columns to its left.
                            if (!getEmployerBurdenShare) return <div className="brio-font-medium" style={{ color: 'var(--brio-grey)' }}>—</div>;
                            const burdenInr = getEmployerBurdenShare(user.id, selectedYear, selectedMonth) || 0;
                            if (burdenInr <= 0) {
                              return <div className="brio-font-medium" style={{ color: 'var(--brio-grey)' }}>—</div>;
                            }
                            const display = currency && currency !== baseCurrency
                              ? formatMoneyInDisplayCurrency(burdenInr, 'INR', fxDate)
                              : fmtCurr(burdenInr);
                            return (
                              <div className="brio-font-medium" style={{ color: 'var(--brio-text-secondary)' }}>
                                {display}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="text-right">
                          <div className="brio-font-medium">
                            {contractedHours !== null ? `${contractedHours}h/month` : '...'}
                          </div>
                        </td>
                        <td className="text-right">
                          {hourlyCost !== null ? (
                            <div className="brio-font-medium" style={{ color: 'var(--brio-gold-hover)' }}>
                              {fmtSal(Math.round(hourlyCost / 10) * 10)}/h
                            </div>
                          ) : (
                            <div className="brio-font-medium" style={{ color: source === 'missing' ? 'var(--brio-orange)' : 'var(--brio-grey)' }}>
                              {source === 'missing' ? '—' : '...'}
                            </div>
                          )}
                        </td>
                        <td className="text-right">
                          <div className="brio-font-medium" style={{ color: 'var(--brio-text-secondary)' }}>
                            {markupFactor.toFixed(2)}×
                          </div>
                        </td>
                        <td className="text-right">
                          {totalMonthlyCost !== null ? (
                            <div
                              className="brio-font-medium"
                              style={{ fontWeight: 600 }}
                              title={isAdjusted ? `Pro-rated: ${prorationNote}` : undefined}
                            >
                              {fmtSal(Math.round(totalMonthlyCost))}
                              {isAdjusted && (
                                <span style={{ fontSize: '9px', color: 'var(--brio-gold-hover)', marginLeft: '3px', fontWeight: 600 }}>pro-rated</span>
                              )}
                            </div>
                          ) : (
                            <div className="brio-font-medium" style={{ color: source === 'missing' ? 'var(--brio-orange)' : 'var(--brio-grey)' }}>
                              {source === 'missing' ? '—' : '...'}
                            </div>
                          )}
                        </td>
                        <td className="text-right">
                          <span
                            className="brio-text-xs"
                            style={sourceBadgeStyle(source)}
                          >
                            {source}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
